import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  HStack,
  Tooltip,
  Code
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import DynamicRuleModal from './DynamicRuleModal';

const DynamicRulesConfig = ({ rules, actions, standardPayers, uniqueCarriers, onSaveRule, onStopRule, isLoading }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatCondition = (condition) => {
    if (!condition) return 'None';
    
    if (condition.operator === 'AND' || condition.operator === 'OR') {
      const subRules = condition.rules || [];
      return (
        <Box>
          <Badge colorScheme={condition.operator === 'AND' ? 'blue' : 'orange'} mr={2}>
            {condition.operator}
          </Badge>
          <Box pl={4} borderLeft="2px solid" borderColor="gray.200" mt={1}>
            {subRules.map((r, idx) => (
              <Box key={idx} mb={1}>
                {formatCondition(r)}
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    let valueDisplay = condition.value;
    if (Array.isArray(condition.value)) {
      valueDisplay = `[${condition.value.join(', ')}]`;
    }

    return (
      <HStack spacing={2} fontSize="sm">
        <Code colorScheme="purple">{condition.field}</Code>
        <Text fontWeight="bold">{condition.operator}</Text>
        <Code colorScheme="green">{valueDisplay}</Code>
      </HStack>
    );
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Box>
          <Heading size="md" color="gray.700">Advanced Dynamic Rules</Heading>
          <Text fontSize="sm" color="gray.500">
            Rules with complex JSON conditions (AND, OR, CONTAINS, NOT IN). These run after Carrier rules and before Post-Rules (Modifiers & Pairs).
          </Text>
        </Box>
        <Button 
          leftIcon={<AddIcon />} 
          colorScheme="blue" 
          onClick={() => setIsModalOpen(true)}
        >
          Create Advanced Rule
        </Button>
      </Flex>

      <Box bg="white" borderRadius="md" boxShadow="sm" borderWidth="1px" p={4}>
        {isLoading ? (
          <Flex justify="center" align="center" p={10}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : rules.length === 0 ? (
          <Text textAlign="center" color="gray.500" p={5}>
            No advanced rules found. Create one to get started.
          </Text>
        ) : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Status</Th>
                <Th>Action</Th>
                <Th>Conditions</Th>
                <Th>Start Date</Th>
                <Th textAlign="right">Manage</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rules.map((rule) => (
                <Tr key={rule.id}>
                  <Td verticalAlign="top" pt={3}>
                    <Badge colorScheme="green">Active</Badge>
                  </Td>
                  <Td verticalAlign="top" pt={3} fontFamily="monospace" fontSize="sm" fontWeight="bold">
                    {rule.action}
                  </Td>
                  <Td verticalAlign="top" pt={3}>
                    {formatCondition(rule.conditions)}
                  </Td>
                  <Td verticalAlign="top" pt={3}>
                    {new Date(rule.effective_start_date).toLocaleDateString()}
                  </Td>
                  <Td textAlign="right" verticalAlign="top" pt={2}>
                    <Tooltip label="Stop Rule">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => onStopRule(rule.id)}
                        aria-label="Stop rule"
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <DynamicRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={onSaveRule}
        availableActions={actions}
        existingPayers={standardPayers}
        existingCarriers={uniqueCarriers}
      />
    </Box>
  );
};

export default DynamicRulesConfig;
